import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import {
  getEmployeeInfo,
  getUsers,
  getRoles,
} from "../../../../utils/services/control/masterDataService";
import {
  assignRole,
  revokeRole,
  adminForceLogout,
} from "../../../../utils/services/control/userManagementService";
import ConfirmDialog from "../common/ConfirmDialog";
import useSnack from "../../hooks/useSnack";

const UserRolesTab = () => {
  const [users, setUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [newRoleId, setNewRoleId] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const { snack, showSnack, closeSnack } = useSnack();

  useEffect(() => {
    const load = async () => {
      setLoadingUsers(true);
      try {
        const [usersData, rolesData] = await Promise.all([
          getUsers(),
          getRoles(),
        ]);
        setUsers(usersData);
        setAllRoles(rolesData);
      } catch (err) {
        console.error("Error loading users/roles:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    load();
  }, []);

  const loadUserRoles = useCallback(
    async (employeeId) => {
      setLoadingRoles(true);
      try {
        const info = await getEmployeeInfo(employeeId);
        const roles = (Array.isArray(info) ? info : [info])
          .filter((row) => row.roleId)
          .map((row) => ({
            roleId: row.roleId,
            roleName: row.roleName,
            roleKey: row.roleKey,
          }));
        setUserRoles(roles);
      } catch (err) {
        showSnack("Error loading user roles", "error");
        setUserRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    },
    [showSnack]
  );

  const handleUserSelect = (_, user) => {
    setSelectedUser(user);
    setNewRoleId("");
    if (user) {
      loadUserRoles(user.employeeId);
    } else {
      setUserRoles([]);
    }
  };

  const handleAssignRole = async () => {
    if (!newRoleId || !selectedUser) return;
    setSaving(true);
    try {
      const res = await assignRole({
        employeeId: selectedUser.employeeId,
        roleId: Number(newRoleId),
      });
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        setNewRoleId("");
        loadUserRoles(selectedUser.employeeId);
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    const { type, data } = confirmAction;
    setSaving(true);
    try {
      let res;
      if (type === "revoke") {
        res = await revokeRole({
          employeeId: selectedUser.employeeId,
          roleId: data.roleId,
        });
      } else if (type === "forceLogout") {
        res = await adminForceLogout({
          targetEmployeeId: selectedUser.employeeId,
          reason: "Admin forced logout from User Roles management",
        });
      }
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else if (type === "revoke") {
        loadUserRoles(selectedUser.employeeId);
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error", "error");
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const availableRoles = allRoles.filter(
    (r) => r.status === 1 && !userRoles.some((ur) => ur.roleId === r.id)
  );

  const userName = `${selectedUser?.firstName || ""} ${selectedUser?.lastName || ""}`.trim();

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Manage Roles by User
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Autocomplete
          options={users}
          getOptionLabel={(u) =>
            `${u.firstName} ${u.lastName} (${u.mail || "—"})`
          }
          value={selectedUser}
          onChange={handleUserSelect}
          loading={loadingUsers}
          sx={{ minWidth: 350 }}
          renderInput={(params) => (
            <TextField {...params} label="Select User" size="small" />
          )}
          isOptionEqualToValue={(opt, val) => opt.employeeId === val.employeeId}
        />
        {selectedUser && (
          <Tooltip title="Force Logout">
            <IconButton
              color="error"
              onClick={() => setConfirmAction({ type: "forceLogout" })}
            >
              <LogoutOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {selectedUser && (
        <>
          {loadingRoles ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell>Key</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userRoles.map((role) => (
                      <TableRow key={role.roleId}>
                        <TableCell>
                          <Chip
                            label={role.roleName}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {role.roleKey}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Revoke Role">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setConfirmAction({ type: "revoke", data: role })
                              }
                            >
                              <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {userRoles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No roles assigned
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Assign Role</InputLabel>
                  <Select
                    value={newRoleId}
                    label="Assign Role"
                    onChange={(e) => setNewRoleId(e.target.value)}
                  >
                    {availableRoles.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAssignRole}
                  disabled={!newRoleId || saving}
                >
                  Assign
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={
          confirmAction?.type === "revoke" ? "Revoke Role" : "Force Logout"
        }
        message={
          confirmAction?.type === "revoke"
            ? `Revoke role "${confirmAction.data?.roleName}" from ${userName}?`
            : `Force logout for ${userName}?`
        }
        confirmLabel={
          confirmAction?.type === "revoke" ? "Revoke" : "Force Logout"
        }
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRolesTab;
